import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Sparkles } from "lucide-react";
import RevealText from "@/components/effects/RevealText";
import FloatingOrbs from "@/components/effects/FloatingOrbs";
import MagneticButton from "@/components/effects/MagneticButton";
import { PROFILE } from "@/data/content";
import { TID } from "@/constants/testIds";

const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Rotator = ({ items }) => {
    const [i, setI] = useState(0);
    useEffect(() => {
        const t = setInterval(() => setI((v) => (v + 1) % items.length), 2400);
        return () => clearInterval(t);
    }, [items.length]);
    return (
        <span className="relative inline-block h-[1.1em] w-[13ch] overflow-hidden align-baseline sm:w-[17ch] md:w-[19ch]">
            {items.map((role, idx) => (
                <motion.span
                    key={role}
                    initial={false}
                    animate={{
                        y: idx === i ? "0%" : idx < i ? "-100%" : "100%",
                        opacity: idx === i ? 1 : 0
                    }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="absolute inset-0 left-0 top-0 whitespace-nowrap text-left"
                >
                    {role}
                </motion.span>
            ))}
        </span>
    );
};

const Hero = () => {
    return (
        <section id="top" className="relative isolate overflow-hidden pt-28 md:pt-40">
            <FloatingOrbs />
            <div aria-hidden className="pointer-events-none absolute inset-0 grid-bg" />
            <div aria-hidden className="pointer-events-none absolute inset-0 grain" />

            <div className="container-x relative">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="flex items-center gap-2"
                >
                    <span className="chip">
                        <Sparkles className="h-3 w-3 text-brand-primary" />
                        Personal · Portfolio · 2026
                    </span>
                </motion.div>

                <h1
                    data-testid={TID.heroHeadline}
                    className="mt-8 text-[13vw] font-semibold leading-[0.95] tracking-[-0.04em] text-white sm:text-[10vw] md:text-[9vw] lg:text-[8.5rem]"
                    style={{ fontFamily: "'General Sans', 'Inter', sans-serif" }}
                >
                    <RevealText text="Mohammad" className="block" />
                    <RevealText
                        text="Naved."
                        className="block"
                        wordClassName="gradient-accent"
                        delay={0.15}
                    />
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 flex flex-col gap-8 md:flex-row md:items-end md:justify-between"
                >
                    <div className="max-w-2xl">
                        <p className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xl font-medium text-white sm:text-2xl md:text-3xl">
                            <Rotator items={PROFILE.roles} />
                            <span className="text-brand-muted">— based on outcomes.</span>
                        </p>
                        <p className="mt-6 max-w-xl text-base text-brand-muted md:text-lg">
                            {PROFILE.tagline}
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <MagneticButton
                            as="button"
                            data-testid={TID.heroGetInTouch}
                            onClick={() => scrollTo("contact")}
                            className="btn-primary"
                        >
                            Get in touch
                            <ArrowUpRight className="h-4 w-4" />
                        </MagneticButton>
                        <MagneticButton
                            as="button"
                            data-testid={TID.heroViewWork}
                            onClick={() => scrollTo("projects")}
                            className="btn-ghost"
                        >
                            View my work
                        </MagneticButton>
                    </div>
                </motion.div>

                {/* Meta strip */}
                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-20 grid grid-cols-2 gap-6 border-t border-white/[.06] pt-8 sm:grid-cols-4"
                >
                    {[
                        { k: "Ventures", v: "3+" },
                        { k: "Projects Shipped", v: "5+" },
                        { k: "Focus", v: "Web · Growth" },
                        { k: "Location", v: "Remote" }
                    ].map((s) => (
                        <div key={s.k}>
                            <p className="overline">{s.k}</p>
                            <p
                                className="mt-2 text-xl font-medium text-white md:text-2xl"
                                style={{ fontFamily: "'General Sans', sans-serif" }}
                            >
                                {s.v}
                            </p>
                        </div>
                    ))}
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1.4, delay: 1.3 }}
                    className="mt-12 flex items-center gap-3 text-xs uppercase tracking-[0.24em] text-brand-muted"
                >
                    <ArrowDown className="h-4 w-4 animate-bounce" />
                    Scroll to explore
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
